import { useEffect, useState } from 'react';
import { budgetsApi, costCentersApi } from '../../api/services';
import type { BudgetDto, BudgetLineDto, BudgetVsActualRowDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Table, fmtMoney, fmtNumber, money, round2, useAction, useAsync,
} from '../../ui/components';
import { AccountPicker } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { PERM, useCan } from '../../ui/access';

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'];

/** Presupuesto anual por cuenta y mes, y comparación contra lo real. */
export function BudgetPage() {
  const budgets = useAsync(() => budgetsApi.list(), []);
  const canEdit = useCan(PERM.setup);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<BudgetDto | null>(null);
  const [view, setView] = useState<'edit' | 'compare'>('compare');
  return (
    <>
      <PageHeader title="Presupuesto" subtitle="Lo planeado contra lo real, por cuenta y mes"
        actions={canEdit ? <Button kind="primary" onClick={() => setCreating(true)}>Nuevo presupuesto</Button> : undefined} />
      <PageHelp page="budget" />
      <Loadable state={budgets}>
        {(rows) => (
          <Table<BudgetDto> rows={rows} rowKey={(b) => b.id} selected={selected?.id ?? null} onRowClick={(b) => setSelected(b)} empty="Todavía no hay presupuestos." columns={[
            { key: 'y', title: 'Año', render: (b) => b.year },
            { key: 'n', title: 'Nombre', render: (b) => b.name },
            { key: 'l', title: 'Cuentas', align: 'right', render: (b) => b.lines },
            { key: 's', title: 'Estado', render: (b) => (b.status === 'A' ? <Badge tone="ok">Activo</Badge> : <Badge>Inactivo</Badge>) },
            { key: 'x', title: '', render: (b) => (
              <>
                <Button kind="link" onClick={() => { setSelected(b); setView('compare'); }}>Comparar con lo real</Button>
                {canEdit && <Button kind="link" onClick={() => { setSelected(b); setView('edit'); }}>Editar montos</Button>}
              </>
            ) },
          ]} />
        )}
      </Loadable>
      {selected && view === 'compare' && <Compare budget={selected} />}
      {selected && view === 'edit' && <Editor budget={selected} onSaved={budgets.reload} />}
      {creating && <CreateModal onClose={() => setCreating(false)} onSaved={(b) => { setCreating(false); budgets.reload(); setSelected(b); setView('edit'); }} />}
    </>
  );
}

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: (b: BudgetDto) => void }) {
  const year = new Date().getFullYear();
  const [f, setF] = useState({ year: year + 1, name: 'Original', copy: true, copyYear: year, adjust: 0 });
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title="Nuevo presupuesto" onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => onSaved(await budgetsApi.create({ year: f.year, name: f.name.trim(), copyActualFromYear: f.copy ? f.copyYear : null, adjustPercent: f.copy ? f.adjust : 0 })),
        { success: 'Presupuesto creado.' })}>
        <Field label="Año"><NumberInput step="1" value={f.year} onChange={(v) => setF({ ...f, year: v })} /></Field>
        <Field label="Nombre" hint="Ej.: Original, Ajustado" error={fieldErrors.Name}><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} maxLength={60} required /></Field>
        <Check checked={f.copy} onChange={(v) => setF({ ...f, copy: v })} label="Partir de lo real de otro año (ingresos, costos y gastos)" />
        {f.copy && (
          <div className="acc-grid-form">
            <Field label="Año real"><NumberInput step="1" value={f.copyYear} onChange={(v) => setF({ ...f, copyYear: v })} /></Field>
            <Field label="Ajuste %" hint="Ej.: 5 = 5 % más"><NumberInput step="0.1" value={f.adjust} onChange={(v) => setF({ ...f, adjust: v })} /></Field>
          </div>
        )}
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Crear</Button></div>
      </Form>
    </Modal>
  );
}

function Editor({ budget, onSaved }: { budget: BudgetDto; onSaved: () => void }) {
  const lines = useAsync(() => budgetsApi.lines(budget.id), [budget.id], true, false);
  const centers = useAsync(() => costCentersApi.list().catch(() => []), []);
  const [rows, setRows] = useState<BudgetLineDto[]>([]);
  const [newAccount, setNewAccount] = useState('');
  const { run, busy } = useAction();
  useEffect(() => { if (lines.data) setRows(lines.data); }, [lines.data]);
  const setMonth = (i: number, m: number, v: number) => setRows(rows.map((r, k) => (k === i ? { ...r, months: r.months.map((x, j) => (j === m ? v : x)) } : r)));
  const hasCenters = (centers.data ?? []).length > 0;
  return (
    <Card title={`Montos de ${budget.name} ${budget.year}`}>
      <Loadable state={lines}>
        {() => (
          <>
            <p className="acc-muted">Escriba los montos con su signo natural (ingresos y gastos en positivo). Use "Repartir" para dividir un total anual en 12 meses iguales.</p>
            <div className="acc-table-wrap">
              <table className="acc-table">
                <thead><tr><th>Cuenta</th>{hasCenters && <th>Centro</th>}{MONTHS.map((m) => <th key={m} style={{ textAlign: 'right' }}>{m}</th>)}<th style={{ textAlign: 'right' }}>Total</th><th /></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.accountNumber}|${r.costCenterId}|${i}`}>
                      <td style={{ minWidth: 180 }}>{r.accountNumber} <span className="acc-muted">{r.accountName}</span></td>
                      {hasCenters && <td style={{ minWidth: 160 }}><Select value={r.costCenterId} onChange={(v) => setRows(rows.map((x, k) => (k === i ? { ...x, costCenterId: v || 0 } : x)))}
                        options={[{ value: 0, label: 'Toda la empresa' }, ...(centers.data ?? []).map((c) => ({ value: c.id, label: c.code }))]} /></td>}
                      {r.months.map((v, m) => <td key={m} style={{ minWidth: 120 }}><NumberInput value={v || null} onChange={(x) => setMonth(i, m, x || 0)} /></td>)}
                      <td style={{ textAlign: 'right' }}><strong>{fmtMoney(r.months.reduce((a, b) => a + b, 0))}</strong></td>
                      <td>
                        <Button kind="link" onClick={() => {
                          const total = r.months.reduce((a, b) => a + b, 0);
                          const each = round2(total / 12);
                          setRows(rows.map((x, k) => (k === i ? { ...x, months: x.months.map((_, j) => (j === 11 ? round2(total - each * 11) : each)) } : x)));
                        }}>Repartir</Button>
                        <Button kind="link" onClick={() => setRows(rows.filter((_, k) => k !== i))}>Quitar</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="acc-toolbar">
              <Field label="Agregar cuenta"><AccountPicker value={newAccount} onChange={setNewAccount} /></Field>
              <Button disabled={!newAccount} onClick={() => { setRows([...rows, { accountNumber: newAccount, costCenterId: 0, months: Array(12).fill(0) }]); setNewAccount(''); }}>Agregar</Button>
            </div>
            <div className="acc-actions">
              <Button kind="primary" busy={busy} onClick={() => run(async () => { const saved = await budgetsApi.saveLines(budget.id, rows); setRows(saved); onSaved(); }, { success: 'Presupuesto guardado.' })}>Guardar</Button>
            </div>
          </>
        )}
      </Loadable>
    </Card>
  );
}

function Compare({ budget }: { budget: BudgetDto }) {
  const centers = useAsync(() => costCentersApi.list().catch(() => []), []);
  const [f, setF] = useState({ from: 1, to: new Date().getFullYear() === budget.year ? new Date().getMonth() + 1 : 12, center: 0 });
  const report = useAsync(() => budgetsApi.vsActual(budget.id, f.from, f.to, f.center), [budget.id, f.from, f.to, f.center]);
  const { run, busy } = useAction();
  const monthOptions = MONTHS.map((m, i) => ({ value: i + 1, label: m }));
  return (
    <Card title={`${budget.name} ${budget.year}: presupuesto contra real`}>
      <div className="acc-toolbar">
        <Field label="Desde"><Select value={f.from} onChange={(v) => setF({ ...f, from: v })} options={monthOptions} /></Field>
        <Field label="Hasta"><Select value={f.to} onChange={(v) => setF({ ...f, to: v })} options={monthOptions} /></Field>
        {(centers.data ?? []).length > 0 && (
          <Field label="Centro de costo"><Select value={f.center} onChange={(v) => setF({ ...f, center: v || 0 })} options={[{ value: 0, label: 'Toda la empresa' }, ...(centers.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))]} /></Field>
        )}
        <Button busy={busy} onClick={() => run(() => budgetsApi.exportVsActual(budget.id, f.from, f.to, f.center))}>Exportar CSV</Button>
      </div>
      <Loadable state={report}>
        {(r) => (
          <>
            <Table<BudgetVsActualRowDto> rows={r.rows} rowKey={(x) => x.accountNumber} empty="Sin presupuesto ni movimientos en esos meses." columns={[
              { key: 'a', title: 'Cuenta', render: (x) => `${x.accountNumber} · ${x.accountName}` },
              money<BudgetVsActualRowDto>('b', 'Presupuesto', (x) => x.budget),
              money<BudgetVsActualRowDto>('r', 'Real', (x) => x.actual),
              money<BudgetVsActualRowDto>('d', 'Diferencia', (x) => x.difference),
              { key: 'p', title: '% ejecutado', align: 'right', render: (x) => (x.percent === null || x.percent === undefined ? '' : `${fmtNumber(x.percent)} %`) },
            ]} />
            {r.notes.map((n) => <Alert key={n}>{n}</Alert>)}
          </>
        )}
      </Loadable>
    </Card>
  );
}
